import { useEffect, useState, type ReactNode } from 'react';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { useReportBackButton } from '../layout/BackButtonContext';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../hooks/useLocale';
import { useWebDrawer } from '../layout/WebDrawerContext';
import {
  WebDrawerMenuButton,
  WEB_HEADER_CONTROL_SIZE,
} from './WebDrawerMenuButton.web';

const MOBILE_QUERY = '(max-width: 767px)';

/** Space between the leading control (back / drawer menu) and the title. */
const LEADING_GAP = 12;

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const fn = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else mq.addListener(fn);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', fn);
      else mq.removeListener(fn);
    };
  }, []);
  return isMobile;
}

export interface WebPageHeaderProps {
  title: string;
  subtitle?: string;
  /** Inline node rendered before the subtitle text (e.g. a verified badge). */
  subtitlePrefix?: ReactNode;
  onBack?: () => void;
  children?: ReactNode;
  /** Whether to render a 1px divider below the header. Default false (Gemini pages don't use header dividers). */
  divider?: boolean;
  /** Compact variant: 18px/500 section-header style. Use inside narrow panels. */
  compact?: boolean;
  /** Top-level/tab-destination routes: drop the back button on mobile (drawer hamburger handles nav). */
  hideBackOnMobile?: boolean;
}

/**
 * Gemini-style content-level page header — the standard title row for every
 * web screen.
 *
 * Non-compact layout (two stacked rows):
 *   Row 1: [← or ☰] Title (wraps if needed)   {children action slot}
 *   Row 2: subtitle, indented under the title
 *
 * Compact layout (single row, narrow panels):
 *   [←] Title 18px         {children}
 *       optional subtitle
 *
 * - `onBack` renders a 40px circle back button with Material Symbols arrow_back.
 * - On phones a top-level route (`hideBackOnMobile`) swaps that slot for the
 *   drawer hamburger, so menu, title and action share one baseline instead of
 *   the shell's floating button drifting above the heading.
 * - `children` is the action slot, aligned next to the title/heading in both layouts.
 * - `divider` (default false) adds a `--gem-outline-variant` bottom border.
 */
export function WebPageHeader({
  title,
  subtitle,
  subtitlePrefix,
  onBack,
  children,
  divider = false,
  compact = false,
  hideBackOnMobile = false,
}: WebPageHeaderProps) {
  const { t } = useTranslation('common');
  const [backHover, setBackHover] = useState(false);
  const { isRTL } = useLocale();
  const isMobile = useIsMobile();
  const drawer = useWebDrawer();
  const effectiveOnBack = hideBackOnMobile && isMobile ? undefined : onBack;
  useReportBackButton(Boolean(effectiveOnBack));
  // Top-level route on a phone: the header owns the drawer affordance. Compact
  // headers sit inside panels, never at the top of a route, so they skip it.
  // Gated on the shell's own flag rather than the media query alone — routes
  // outside the protected shell (/cv-share, /cv-access, /cv-grant) have no
  // drawer, and an empty slot would still have indented the subtitle.
  const showDrawerButton =
    Boolean(drawer?.isMobile) && !effectiveOnBack && !compact;

  const titleSize = compact ? 18 : isMobile ? 20 : 32;
  const titleWeight = compact || isMobile ? 500 : 400;
  // A 40px first line box makes the title's first line share its centre with
  // the 40px controls either side of it — so a heading that wraps to two lines
  // still leaves the menu and action button on line one.
  const titleLineHeight = compact ? 1.2 : `${WEB_HEADER_CONTROL_SIZE}px`;
  const subtitleSize = 13;
  const headerPadding = 8;
  const headerMargin = compact ? 12 : 16;

  const backButton = effectiveOnBack ? (
    <button
      type="button"
      onClick={effectiveOnBack}
      onMouseEnter={() => setBackHover(true)}
      onMouseLeave={() => setBackHover(false)}
      aria-label={t('actions.back')}
      style={{
        width: WEB_HEADER_CONTROL_SIZE,
        height: WEB_HEADER_CONTROL_SIZE,
        borderRadius: '50%',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        backgroundColor: backHover ? 'var(--hover-strong)' : 'transparent',
        color: 'var(--text-secondary)',
        flexShrink: 0,
        transition: 'background-color 150ms ease',
        fontFamily: 'inherit',
      }}
    >
      <MaterialSymbol name={isRTL ? 'arrow_forward' : 'arrow_back'} size={20} />
    </button>
  ) : null;

  // Back and hamburger are mutually exclusive and share the same 40px slot, so
  // the title lands on the same x-position either way.
  const leadingControl = backButton ?? (showDrawerButton ? <WebDrawerMenuButton /> : null);

  const titleNode = (
    <div
      style={{
        // Google Sans has no Arabic letters: without Cairo next, an Arabic
        // title fell through to the platform's (often calligraphic) face.
        fontFamily: compact
          ? 'inherit'
          : "'Google Sans', 'Google Sans Text', 'Cairo', sans-serif",
        fontSize: titleSize,
        fontWeight: titleWeight,
        lineHeight: titleLineHeight,
        color: 'var(--text-primary)',
        minWidth: 0,
        flex: 1,
        // Allow wrap; never truncate.
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
      }}
    >
      {title}
    </div>
  );

  const subtitleNode = subtitle ? (
    <div
      style={{
        fontSize: subtitleSize,
        lineHeight: '18px',
        color: 'var(--text-tertiary)',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: subtitlePrefix ? 5 : 0,
      }}
    >
      {subtitlePrefix}
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{subtitle}</span>
    </div>
  ) : null;

  // The action rides the same 40px band as the leading control and the title's
  // first line, so a shorter chip still shares their centreline — and a title
  // that wraps to two lines leaves the action pinned to the first.
  const actionSlot = children ? (
    <div
      style={{
        flexShrink: 0,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        minHeight: compact ? undefined : WEB_HEADER_CONTROL_SIZE,
      }}
    >
      {children}
    </div>
  ) : null;

  // Compact: single row (back + title on left, action on right).
  if (compact) {
    return (
      <header className="tabbio-page-header"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          paddingBottom: headerPadding,
          marginBottom: headerMargin,
          borderBottom: divider
            ? '1px solid var(--gem-outline-variant)'
            : 'none',
          fontFamily:
            "'Google Sans Text', 'Google Sans', 'Cairo', Roboto, Arial, sans-serif",
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
            flex: 1,
          }}
        >
          {leadingControl}
          <div style={{ minWidth: 0, flex: 1 }}>
            {titleNode}
            {subtitleNode ? (
              <div style={{ marginTop: 4 }}>{subtitleNode}</div>
            ) : null}
          </div>
        </div>
        {actionSlot}
      </header>
    );
  }

  // Non-compact: title + action share row 1; subtitle sits alone on row 2, under the title.
  return (
    <header className="tabbio-page-header"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: subtitleNode ? 8 : 0,
        paddingBottom: headerPadding,
        marginBottom: headerMargin,
        borderBottom: divider
          ? '1px solid var(--gem-outline-variant)'
          : 'none',
        fontFamily:
          "'Google Sans Text', 'Google Sans', 'Cairo', Roboto, Arial, sans-serif",
      }}
    >
      {/* Row 1: back/menu + title + action, action aligned next to the heading. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            // Top-aligned, not centred: a wrapped title must grow downwards
            // and leave the leading control on the first line.
            alignItems: 'flex-start',
            gap: LEADING_GAP,
            minWidth: 0,
            flex: 1,
            // Hold the band even with no leading control, so headings with and
            // without a back button put their action button at the same height.
            minHeight: WEB_HEADER_CONTROL_SIZE,
          }}
        >
          {leadingControl}
          {titleNode}
        </div>
        {actionSlot}
      </div>

      {/* Row 2: subtitle only, under the title (clear the leading control). */}
      {subtitleNode ? (
        <div
          style={{
            paddingInlineStart: leadingControl
              ? WEB_HEADER_CONTROL_SIZE + LEADING_GAP
              : 0,
            minWidth: 0,
          }}
        >
          {subtitleNode}
        </div>
      ) : null}
    </header>
  );
}
